import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { campaignService } from '@/services/api';

export const useCampaign = (campaignId: string | null) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useQuery({
    queryKey: queryKeys.campaigns.byId(campaignId || ''),
    queryFn: () =>
      campaignService.getCampaign({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        campaign_id: campaignId!,
      }),
    enabled: !!campaignId && !!selectedOrganizationId && !!selectedProgramId,
    staleTime: 10 * 1000, // 10 seconds
    gcTime: 10 * 60 * 1000, // 10 minutes
    select: data => data.campaign,
  });
};

export const useCampaignsByWorkflow = (
  workflowId: string | null,
  params?: {
    limit?: number;
    start_after_id?: string;
  },
) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useQuery({
    queryKey: queryKeys.campaigns.byWorkflow(workflowId || '', params),
    queryFn: () =>
      campaignService.listCampaigns({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        workflow_id: workflowId!,
        limit: params?.limit || 20,
        start_after_id: params?.start_after_id,
      }),
    enabled: !!workflowId && !!selectedOrganizationId && !!selectedProgramId,
    staleTime: 10 * 1000, // 10 seconds
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useCreateCampaign = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation({
    mutationFn: (data: { name: string; workflow_id: string; file_data?: File | Blob }) =>
      campaignService.createCampaign({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        name: data.name,
        workflow_id: data.workflow_id,
        // The generated service handles File/Blob conversion to FormData internally
        // Cast is safe because the service wraps non-Uint8Array in Blob
        file_data: data.file_data as unknown as Uint8Array | undefined,
      }),
    onSuccess: data => {
      // Invalidate the list queries to refresh the table
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.all,
      });

      return data;
    },
  });
};
